import { useCallback, useEffect, useState, type FormEvent } from "react";
import { TenantApiError } from "@qr-menu/api-tenant";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
} from "@qr-menu/ui";
import { tenantApi } from "../lib/api";

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready" };

const NAME_MIN = 2;
const NAME_MAX = 150;
const ADDRESS_MAX = 500;
const PHONE_MAX = 30;
const LOGO_URL_MAX = 2048;
const LOCATION_URL_MAX = 2048;

// TenantApiError.message carries the backend's curated public error_message,
// never a raw cause or response body — same helper as Account/Appearance.
function describe(err: unknown, fallback: string): string {
  if (err instanceof TenantApiError) {
    return err.message;
  }
  return fallback;
}

// No client-side role gating: an editor can open this page and see the
// current values (GET /businesses/current already permits owner/admin/
// editor), but Kaydet on an editor account gets the same backend 403 as any
// other role-restricted mutation in this app, surfaced through the same
// generic describe() path used everywhere else — see Appearance.tsx's own
// note on this convention.
export function BusinessRoute() {
  const [state, setState] = useState<State>({ kind: "loading" });

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [locationUrl, setLocationUrl] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const business = await tenantApi.currentBusiness();
      setName(business.name);
      setAddress(business.address);
      setPhone(business.phone);
      setLogoUrl(business.logo_url);
      setLocationUrl(business.location_url);
      setState({ kind: "ready" });
    } catch (err) {
      setState({
        kind: "error",
        message: describe(err, "İşletme bilgileri alınamadı."),
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // A change after a successful (or failed) save clears the stale feedback
  // banner: the on-screen values no longer match what was last attempted
  // until the next Kaydet — same principle as Appearance.tsx.
  function clearFeedback() {
    setSaveSuccess(false);
    setSaveError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;

    setSaveError(null);
    setSaveSuccess(false);

    const trimmedName = name.trim();
    if (trimmedName.length < NAME_MIN || trimmedName.length > NAME_MAX) {
      setSaveError(
        `İşletme adı en az ${NAME_MIN}, en fazla ${NAME_MAX} karakter olmalıdır.`,
      );
      return;
    }

    const trimmedAddress = address.trim();
    if (trimmedAddress.length > ADDRESS_MAX) {
      setSaveError(`Adres en fazla ${ADDRESS_MAX} karakter olabilir.`);
      return;
    }

    const trimmedPhone = phone.trim();
    if (trimmedPhone.length > PHONE_MAX) {
      setSaveError(`Telefon en fazla ${PHONE_MAX} karakter olabilir.`);
      return;
    }

    const trimmedLogoUrl = logoUrl.trim();
    if (trimmedLogoUrl.length > LOGO_URL_MAX) {
      setSaveError(`Logo URL en fazla ${LOGO_URL_MAX} karakter olabilir.`);
      return;
    }
    // Modest syntax check only, matching the backend's existing http_url
    // policy (http OR https, not https-only): no fragment/credential/
    // fetchability rules here — those would be stricter than what
    // PUT /businesses/current/profile actually accepts, and a real
    // rejection is left to the backend's 422 rather than a hand-rolled
    // second validator that could drift from it.
    if (trimmedLogoUrl !== "") {
      let parsedUrl: URL;
      try {
        parsedUrl = new URL(trimmedLogoUrl);
      } catch {
        setSaveError("Logo URL geçerli bir adres olmalıdır.");
        return;
      }
      if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
        setSaveError("Logo URL http:// veya https:// ile başlamalıdır.");
        return;
      }
    }

    const trimmedLocationUrl = locationUrl.trim();
    if (trimmedLocationUrl.length > LOCATION_URL_MAX) {
      setSaveError(`Konum linki en fazla ${LOCATION_URL_MAX} karakter olabilir.`);
      return;
    }
    // Modest syntax check only, matching the backend's stricter (https-only)
    // location_url policy — this deliberately does not duplicate the
    // backend's full normalizer (no userinfo/host inspection, no unicode
    // whitespace/control scanning, no DNS checks, no provider whitelist):
    // those remain backend authority, and a real rejection is left to the
    // 422 rather than a hand-rolled second validator that could drift from
    // it.
    if (trimmedLocationUrl !== "") {
      let parsedLocationUrl: URL;
      try {
        parsedLocationUrl = new URL(trimmedLocationUrl);
      } catch {
        setSaveError("Konum linki geçerli bir adres olmalıdır.");
        return;
      }
      if (parsedLocationUrl.protocol !== "https:") {
        setSaveError("Konum linki https:// ile başlamalıdır.");
        return;
      }
    }

    setSubmitting(true);
    try {
      // The backend returns the server-normalized (trimmed) values in the
      // same response; use them directly instead of issuing a second GET.
      const business = await tenantApi.updateBusinessProfile({
        name: trimmedName,
        address: trimmedAddress,
        phone: trimmedPhone,
        logo_url: trimmedLogoUrl,
        location_url: trimmedLocationUrl,
      });
      setName(business.name);
      setAddress(business.address);
      setPhone(business.phone);
      setLogoUrl(business.logo_url);
      setLocationUrl(business.location_url);
      setSaveSuccess(true);
    } catch (err) {
      // A bare 401 means the token itself was already invalid: the client's
      // onUnauthorized has already cleared the session and is navigating to
      // /login, so there is nothing further to show here — same guard as
      // Account.tsx/Appearance.tsx.
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setSaveError(describe(err, "İşletme bilgileri kaydedilemedi."));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (state.kind === "loading") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>İşletme</CardTitle>
          <CardDescription>
            İşletmenizin genel menüde görünen temel bilgilerini buradan
            yönetin.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="state">Yükleniyor…</div>
        </CardContent>
      </Card>
    );
  }

  if (state.kind === "error") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>İşletme</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="state error">{state.message}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>İşletme</CardTitle>
        <CardDescription>
          İşletmenizin genel menüde görünen temel bilgilerini buradan
          yönetin.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="appearance-form" onSubmit={handleSubmit}>
          <Field label="İşletme adı" htmlFor="business-name">
            <input
              id="business-name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearFeedback();
              }}
              maxLength={NAME_MAX}
              disabled={submitting}
              required
            />
          </Field>
          <Field label="Adres" htmlFor="business-address">
            <input
              id="business-address"
              type="text"
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
                clearFeedback();
              }}
              maxLength={ADDRESS_MAX}
              placeholder="Opsiyonel"
              disabled={submitting}
            />
          </Field>
          <Field
            label="Konum linki"
            htmlFor="business-location-url"
            description="Google Maps veya başka bir harita servisindeki konum bağlantısı."
          >
            <input
              id="business-location-url"
              type="text"
              inputMode="url"
              value={locationUrl}
              onChange={(e) => {
                setLocationUrl(e.target.value);
                clearFeedback();
              }}
              maxLength={LOCATION_URL_MAX}
              placeholder="https://maps.app.goo.gl/..."
              disabled={submitting}
            />
          </Field>
          <Field label="Telefon" htmlFor="business-phone">
            <input
              id="business-phone"
              type="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                clearFeedback();
              }}
              maxLength={PHONE_MAX}
              placeholder="Opsiyonel"
              disabled={submitting}
            />
          </Field>
          <Field label="Logo URL" htmlFor="business-logo-url">
            <input
              id="business-logo-url"
              type="text"
              inputMode="url"
              value={logoUrl}
              onChange={(e) => {
                setLogoUrl(e.target.value);
                clearFeedback();
              }}
              maxLength={LOGO_URL_MAX}
              placeholder="https://example.com/logo.png"
              disabled={submitting}
            />
          </Field>

          {saveError && <div className="state error">{saveError}</div>}
          {saveSuccess && (
            <div className="state ok">İşletme bilgileri kaydedildi.</div>
          )}

          <div className="login-actions">
            <button type="submit" className="primary" disabled={submitting}>
              {submitting ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
